import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService } from '../services/orderService';
import { getProductPrice, getCommission, getDeliveryFee, formatPrice } from '../utils/priceHelpers';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const userId = user?.id || localStorage.getItem('userId');
      
      if (!userId) {
        navigate('/login');
        return;
      }
      const response = await orderService.getUserOrders(userId);

      if (response.success) {
        setOrders(response.orders || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Commandée': '📝',
      'Payée': '💳',
      'Expédiée': '🚚',
      'Livrée': '✅',
      'Annulée': '❌',
    };
    return icons[status] || '❓';
  };

  const getDeliveryIcon = (status) => {
    const icons = {
      'Non expédié': '📦',
      'En transit': '🚚',
      'Arrivé en RDC': '📦',
      'Prêt à être livré': '🏠',
      'Livré': '✅',
    };
    return icons[status] || '❓';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Commandée': '#FF0000',
      'Payée': '#28a745',
      'Expédiée': '#ffc107',
      'Livrée': '#28a745',
      'Annulée': '#dc3545',
    };
    return colors[status] || '#6c757d';
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">📊 Mes Commandes</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/order')}
          >
            + Nouvelle Commande
          </button>
        </div>

      {orders.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
            Aucune commande pour le moment
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/order')}
          >
            Créer ma première commande
          </button>
        </div>
      ) : (
        <div>
          {orders.map((order) => {
            // Vérifier si c'est une commande avec plusieurs produits (nouveau format)
            // Si la commande a totalProductPrice, totalCommission, totalDeliveryFee, c'est le nouveau format
            // Sinon, si elle a productPrice, commission, deliveryFee, c'est l'ancien format
            const isLocal = order.orderType === 'local';
            const hasMultipleProducts = (order.items && Array.isArray(order.items) && order.items.length > 0) 
              || (order.totalProductPrice != null || order.totalCommission != null || order.totalDeliveryFee != null);
            const products = hasMultipleProducts ? order.items : [{
              imageUrl: order.imageUrl,
              productLink: order.productLink,
              size: order.size,
              color: order.color,
              quantity: order.quantity,
              comment: order.comment,
              verifiedProductName: order.verifiedProductName,
              productPrice: order.productPrice,
              commission: order.commission,
              subtotal: order.productPrice && order.commission ? order.productPrice + order.commission : null,
            }];

            return (
              <div key={order._id} className="card order-card">
                <div className="order-card-content">
                  <div style={{ flex: 1 }}>
                    <h3 style={{ marginBottom: '10px', color: '#333' }}>
                      Commande #{order._id.slice(-8).toUpperCase()}
                      {isLocal && (
                        <span style={{
                          fontSize: 12,
                          marginLeft: 10,
                          background: '#e8f5e9',
                          color: '#2e7d32',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontWeight: 600,
                        }}>
                          Boutique locale
                        </span>
                      )}
                      {!isLocal && (
                        <span style={{
                          fontSize: 12,
                          marginLeft: 10,
                          background: '#fff3e0',
                          color: '#e65100',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontWeight: 600,
                        }}>
                          International
                        </span>
                      )}
                      {hasMultipleProducts && (
                        <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                          ({order.items.length} produit{order.items.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </h3>
                    <p style={{ color: '#666', marginBottom: '15px' }}>
                      <strong>Plateforme:</strong> {order.platform}
                    </p>

                    {/* Liste des produits */}
                    <div style={{ marginBottom: '15px' }}>
                      {products.map((product, index) => (
                        <div key={index} style={{ 
                          marginBottom: '15px', 
                          padding: '15px', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '8px',
                          border: '1px solid #e8e8e8'
                        }}>
                          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                            {product.imageUrl && (
                              <img
                                src={product.imageUrl}
                                alt={`Produit ${index + 1}`}
                                style={{
                                  width: '80px',
                                  height: '80px',
                                  borderRadius: '8px',
                                  border: '1px solid #ddd',
                                  objectFit: 'cover',
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <h4 style={{ marginBottom: '8px', color: '#333', fontSize: '16px' }}>
                                {product.productName || product.verifiedProductName || `Produit ${index + 1}`}
                              </h4>
                              {product.size && (
                                <p style={{ color: '#666', marginBottom: '3px', fontSize: '14px' }}>
                                  <strong>Taille:</strong> {product.size}
                                </p>
                              )}
                              {product.color && (
                                <p style={{ color: '#666', marginBottom: '3px', fontSize: '14px' }}>
                                  <strong>Couleur:</strong> {product.color}
                                </p>
                              )}
                              <p style={{ color: '#666', marginBottom: '3px', fontSize: '14px' }}>
                                <strong>Quantité:</strong> {product.quantity}
                              </p>
                              {product.productLink && (
                                <a
                                  href={product.productLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#FF0000', textDecoration: 'none', fontSize: '12px', fontWeight: '500' }}
                                >
                                  Voir le produit →
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Détail du prix */}
                    {order.totalAmount ? (
                      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                        <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: 'bold', color: '#0369a1' }}>
                          💰 Détail du prix:
                        </p>
                        {(() => {
                          const productPrice = getProductPrice(order);
                          const commission = getCommission(order);
                          const deliveryFee = getDeliveryFee(order);
                          
                          return (
                            <>
                              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                <strong>Prix {hasMultipleProducts ? 'total produits' : 'produit'}:</strong> ${formatPrice(productPrice)}
                              </p>
                              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                <strong>Commission{hasMultipleProducts ? ' totale' : ''}:</strong> ${formatPrice(commission)}
                              </p>
                              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                <strong>Frais livraison:</strong> ${formatPrice(deliveryFee)}
                              </p>
                            </>
                          );
                        })()}
                        <p style={{ margin: '10px 0 5px 0', fontSize: '16px', fontWeight: 'bold', color: '#FF0000', borderTop: '1px solid #bae6fd', paddingTop: '5px' }}>
                          <strong>Total:</strong> ${order.totalAmount.toFixed(2)}
                        </p>
                      </div>
                    ) : !isLocal ? (
                      <p style={{ color: '#999', marginTop: '10px', fontSize: '14px', fontStyle: 'italic' }}>
                        ⏳ Prix en attente de validation par un agent
                      </p>
                    ) : null}
                    {order.totalAmount &&
                      order.paymentStatus !== 'Payé' &&
                      order.paymentMethod !== 'cash_on_delivery' && (
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/payment/${order._id}`)}
                        style={{
                          marginTop: '15px',
                          padding: '10px 20px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                        }}
                      >
                        💳 Payer maintenant
                      </button>
                    )}
                    {order.paymentMethod === 'cash_on_delivery' && order.paymentStatus !== 'Payé' && (
                      <div style={{
                        marginTop: '15px',
                        padding: '10px',
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: '8px',
                        color: '#f57f17',
                        fontWeight: '600',
                      }}>
                        💵 Paiement à la livraison
                      </div>
                    )}
                    {order.paymentStatus === 'Payé' && (
                      <div style={{
                        marginTop: '15px',
                        padding: '10px',
                        backgroundColor: '#d4edda',
                        border: '1px solid #c3e6cb',
                        borderRadius: '8px',
                        color: '#155724',
                        fontWeight: '600',
                      }}>
                        ✅ Paiement effectué
                      </div>
                    )}
                  </div>
                </div>

              {/* Timeline des statuts */}
              <div className="order-status-timeline">
                <div className="order-status-items">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{getStatusIcon(order.status)}</span>
                    <span style={{ color: getStatusColor(order.status), fontWeight: '600' }}>
                      {order.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>💳</span>
                    <span style={{ color: '#666' }}>{order.paymentStatus}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{getDeliveryIcon(order.deliveryStatus)}</span>
                    <span style={{ color: '#666' }}>{order.deliveryStatus}</span>
                  </div>
                </div>
              </div>

              {order.trackingId && (
                <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#0369a1', fontSize: '14px' }}>
                        🏷️ Tracking ID:
                      </p>
                      <p style={{ margin: '0', fontFamily: 'monospace', fontSize: '16px', fontWeight: 'bold', color: '#0369a1' }}>
                        {order.trackingId}
                      </p>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/tracking/${order._id}`)}
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        backgroundColor: '#FF0000',
                      }}
                    >
                      📦 Voir le suivi détaillé
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;

